import React, { useState, useEffect } from 'react';
import { User, CreditCard, Lock, Bell, Trash2, CheckCircle, AlertCircle, ExternalLink, Phone, Mail, Building } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('account');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Account form state
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Billing state
  const [trialStatus, setTrialStatus] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState('');

  useEffect(() => {
    fetchCustomer();
    fetchTrialStatus();
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('cia_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const fetchCustomer = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, { headers: getHeaders() });
      const data = await res.json();
      if (data.customer) {
        setCustomer(data.customer);
        setName(data.customer.name || '');
        setBusinessName(data.customer.business_name || '');
        setEmail(data.customer.email || '');
        setPhone(data.customer.phone || '');
        setTimezone(data.customer.timezone || 'America/New_York');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/calls/trial-status`, { headers: getHeaders() });
      const data = await res.json();
      setTrialStatus(data);
    } catch (e) {}
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const saveAccount = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/auth/update`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ name, business_name: businessName, timezone }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage('Account updated successfully');
        fetchCustomer();
      } else {
        showMessage(data.error || 'Update failed', 'error');
      }
    } catch (e) {
      showMessage('Connection failed', 'error');
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      showMessage('Passwords do not match', 'error');
      return;
    }
    if (newPassword.length < 8) {
      showMessage('Password must be at least 8 characters', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage('Password changed successfully');
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        showMessage(data.error || 'Password change failed', 'error');
      }
    } catch (e) {
      showMessage('Connection failed', 'error');
    }
    setSaving(false);
  };

  const startCheckout = async (plan) => {
    setCheckoutLoading(plan);
    try {
      const res = await fetch(`${API_BASE}/stripe/create-checkout`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ plan }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        showMessage(data.error || 'Could not start checkout', 'error');
      }
    } catch (e) {
      showMessage('Connection failed', 'error');
    }
    setCheckoutLoading('');
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'danger', label: 'Danger Zone', icon: Trash2 },
  ];

  const plans = [
    {
      id: 'starter', name: 'Starter', price: 49,
      features: ['1 agent', 'Call capture & recording', 'AI transcription', 'SMS & email notifications', 'Built-in CRM'],
    },
    {
      id: 'growth', name: 'Growth', price: 99, popular: true,
      features: ['3 agents', 'All Starter features', 'Team routing & round-robin', 'Agent performance reports', 'Business hours control'],
    },
    {
      id: 'professional', name: 'Professional', price: 199,
      features: ['10 agents', 'All Growth features', 'Simultaneous ring', 'Advanced analytics', '24/7 priority support'],
    },
  ];

  const timezones = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
    'Australia/Sydney',
  ];

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const currentPlan = customer?.plan || 'starter';
  const isPaid = !!customer?.stripe_customer_id;

  return (
    <div className="page-wrap" style={{ maxWidth: 900 }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Settings</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
          Manage your account, billing, and preferences
        </p>
      </div>

      {/* Message banner */}
      {message && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', borderRadius: 8, marginBottom: 20,
          background: message.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(0,200,150,0.1)',
          border: `1px solid ${message.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(0,200,150,0.3)'}`,
          color: message.type === 'error' ? '#ef4444' : 'var(--green)',
        }}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          <span style={{ fontSize: 13 }}>{message.text}</span>
        </div>
      )}

      <div className="settings-wrap">

        {/* Tabs nav */}
        <div className="settings-tabs-nav">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isDanger = tab.id === 'danger';
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`settings-tab-btn${isActive ? ' active' : ''}`}
                style={{ color: isDanger ? '#ef4444' : isActive ? 'var(--text-primary)' : 'var(--text-muted)' }}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* ── ACCOUNT TAB ── */}
          {activeTab === 'account' && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 20 }}>Account Information</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="ob-field">
                  <label className="ob-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <User size={13} /> Full Name
                  </label>
                  <input className="ob-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
                </div>

                <div className="ob-field">
                  <label className="ob-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Building size={13} /> Business Name
                  </label>
                  <input className="ob-input" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Your business name" />
                </div>

                <div className="ob-field">
                  <label className="ob-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Mail size={13} /> Email Address
                  </label>
                  <input className="ob-input" value={email} disabled style={{ opacity: 0.6 }} />
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Contact support to change your email</p>
                </div>

                <div className="ob-field">
                  <label className="ob-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Phone size={13} /> Phone Number
                  </label>
                  <input className="ob-input" value={phone} disabled style={{ opacity: 0.6 }} />
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Contact support to change your phone number</p>
                </div>

                <div className="ob-field">
                  <label className="ob-label">Timezone</label>
                  <select
                    className="ob-input"
                    value={timezone}
                    onChange={e => setTimezone(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    {timezones.map(tz => (
                      <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>

                <button
                  className="ob-btn ob-btn-primary"
                  onClick={saveAccount}
                  disabled={saving}
                  style={{ alignSelf: 'flex-start', padding: '10px 24px' }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* ── BILLING TAB ── */}
          {activeTab === 'billing' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Current plan */}
              <div className="card">
                <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Current Plan</h2>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{
                        fontSize: 18, fontWeight: 700, color: 'var(--text-primary)',
                        textTransform: 'capitalize',
                      }}>{currentPlan}</span>
                      <span style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 99,
                        background: isPaid ? 'rgba(0,200,150,0.1)' : 'rgba(245,158,11,0.1)',
                        color: isPaid ? 'var(--green)' : '#f59e0b',
                        fontWeight: 600,
                      }}>
                        {isPaid ? 'Active' : 'Trial'}
                      </span>
                    </div>
                    {trialStatus && !isPaid && (
                      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>
                        {trialStatus.reason === 'trial_expired'
                          ? 'Your trial has expired'
                          : `${trialStatus.days_remaining} day${trialStatus.days_remaining === 1 ? '' : 's'} remaining in trial`}
                      </p>
                    )}
                  </div>
                  {isPaid && (
                    <button
                      className="ob-btn ob-btn-ghost"
                      style={{ fontSize: 13, padding: '8px 16px' }}
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_BASE}/stripe/create-portal`, {
                            method: 'POST',
                            headers: getHeaders(),
                          });
                          const data = await res.json();
                          if (data.portal_url) {
                            window.location.href = data.portal_url;
                          } else {
                            showMessage(data.error || 'Could not open billing portal', 'error');
                          }
                        } catch (e) {
                          showMessage('Connection failed', 'error');
                        }
                      }}
                    >
                      Manage billing <ExternalLink size={12} style={{ marginLeft: 4 }} />
                    </button>
                  )}
                </div>
              </div>

              {/* Plan cards */}
              <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {isPaid ? 'Change Plan' : 'Choose a Plan'}
              </h2>

              <div className="plan-grid">
                {plans.map(plan => {
                  const isCurrent = currentPlan === plan.id && isPaid;
                  return (
                    <div key={plan.id} style={{
                      border: `1px solid ${plan.popular ? 'var(--blue)' : 'var(--border)'}`,
                      borderRadius: 10,
                      padding: 16,
                      position: 'relative',
                      background: isCurrent ? 'rgba(0,200,150,0.05)' : 'var(--bg-secondary)',
                    }}>
                      {plan.popular && (
                        <div style={{
                          position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                          background: 'var(--blue)', color: '#fff',
                          fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 99,
                          whiteSpace: 'nowrap',
                        }}>
                          Most Popular
                        </div>
                      )}
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{plan.name}</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
                        ${plan.price}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span>
                      </div>
                      <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {plan.features.map(f => (
                          <li key={f} style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={12} color="var(--green)" style={{ flexShrink: 0 }} />
                            {f}
                          </li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <div style={{
                          textAlign: 'center', padding: '8px', borderRadius: 6,
                          background: 'rgba(0,200,150,0.1)', color: 'var(--green)',
                          fontSize: 12, fontWeight: 600,
                        }}>
                          Current Plan
                        </div>
                      ) : (
                        <button
                          onClick={() => startCheckout(plan.id)}
                          disabled={!!checkoutLoading}
                          style={{
                            width: '100%', padding: '9px', borderRadius: 6,
                            border: 'none', cursor: 'pointer',
                            background: plan.popular ? 'var(--blue)' : 'var(--bg-hover)',
                            color: plan.popular ? '#fff' : 'var(--text-primary)',
                            fontSize: 13, fontWeight: 600,
                            opacity: checkoutLoading && checkoutLoading !== plan.id ? 0.5 : 1,
                          }}
                        >
                          {checkoutLoading === plan.id ? 'Loading...' : isPaid ? `Switch to ${plan.name}` : `Start ${plan.name}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── SECURITY TAB ── */}
          {activeTab === 'security' && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 20 }}>Change Password</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 400 }}>
                <div className="ob-field">
                  <label className="ob-label">Current Password</label>
                  <input
                    className="ob-input" type="password"
                    value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                  />
                </div>
                <div className="ob-field">
                  <label className="ob-label">New Password</label>
                  <input
                    className="ob-input" type="password"
                    value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                  />
                </div>
                <div className="ob-field">
                  <label className="ob-label">Confirm New Password</label>
                  <input
                    className="ob-input" type="password"
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                  />
                </div>
                <button
                  className="ob-btn ob-btn-primary"
                  onClick={changePassword}
                  disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                  style={{ alignSelf: 'flex-start', padding: '10px 24px' }}
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          )}

          {/* ── NOTIFICATIONS TAB ── */}
          {activeTab === 'notifications' && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 20 }}>Notification Preferences</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  { label: 'Call summary after every call', desc: 'Receive a summary via SMS or email after each call', defaultOn: true },
                  { label: 'Missed call alerts', desc: 'Get notified immediately when you miss a call', defaultOn: true },
                  { label: 'High urgency call alerts', desc: 'Instant notification for high urgency calls', defaultOn: true },
                  { label: 'Daily call digest', desc: 'A daily summary of all calls at end of business day', defaultOn: false },
                  { label: 'Follow-up reminders', desc: 'Reminders when a follow-up is due', defaultOn: true },
                  { label: 'New sales lead alerts', desc: 'Instant alert when a call is classified as a sales lead', defaultOn: true },
                ].map((notif, i) => (
                  <NotifToggle key={i} {...notif} />
                ))}
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 16 }}>
                Notifications are sent via SMS for US numbers and WhatsApp for international numbers.
              </p>
            </div>
          )}

          {/* ── DANGER ZONE TAB ── */}
          {activeTab === 'danger' && (
            <div className="card" style={{ border: '1px solid rgba(239,68,68,0.3)' }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, color: '#ef4444', marginBottom: 8 }}>Danger Zone</h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
                These actions are permanent and cannot be undone.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px', border: '1px solid var(--border)', borderRadius: 8,
                  flexWrap: 'wrap', gap: 12,
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Export my data</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Download all your calls, transcripts, and contacts</div>
                  </div>
                  <button
                    className="ob-btn ob-btn-ghost"
                    style={{ fontSize: 13, padding: '8px 16px', whiteSpace: 'nowrap' }}
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_BASE}/auth/export-data`, {
                          headers: getHeaders(),
                        });
                        if (!res.ok) {
                          showMessage('Export failed', 'error');
                          return;
                        }
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `aizaan-export-${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        showMessage('Data exported successfully');
                      } catch (e) {
                        showMessage('Export failed', 'error');
                      }
                    }}
                  >
                    Export data
                  </button>
                </div>

                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8,
                  flexWrap: 'wrap', gap: 12,
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#ef4444', marginBottom: 4 }}>Delete account</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Permanently delete your account, all calls, recordings, and data. This cannot be undone.
                    </div>
                  </div>
                  <button
                    style={{
                      padding: '8px 16px', borderRadius: 6, border: '1px solid #ef4444',
                      background: 'transparent', color: '#ef4444', cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                    }}
                    onClick={() => {
                      if (window.confirm('Are you absolutely sure? This will permanently delete your account and all data. Type DELETE to confirm.') ) {
                        alert('Please contact support@aizaan.app to complete account deletion.');
                      }
                    }}
                  >
                    Delete account
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function NotifToggle({ label, desc, defaultOn }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 0', borderBottom: '1px solid var(--border)', gap: 16,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{desc}</div>
      </div>
      <div
        onClick={() => setOn(!on)}
        style={{
          width: 40, height: 22, borderRadius: 99,
          background: on ? 'var(--green)' : 'var(--border)',
          position: 'relative', cursor: 'pointer', flexShrink: 0,
          transition: 'background 0.2s',
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: on ? 21 : 3,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s',
        }} />
      </div>
    </div>
  );
}
